/**
 * Back testing's landing: the protocols it can test, each listed by the SOP that holds its text,
 * searched and filtered as the Protocols page lists them. Picking one opens its scenario types.
 */

import { useMemo, useState } from 'react';
import { Ticket, Tickets } from 'lucide-react';
import { FilterMenu } from '@/components/shared/filter-menu';
import { DataTable } from '@/components/ui/data-table';
import { SearchInput } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { matchesAllTerms, searchTerms } from '@/lib/search';
import { ProtocolCategoryTabs, type CategoryFilter } from '@/modules/protocols/components/protocol-category-tabs';
import { AnchoredPopover, MenuGroup, MenuItem, MenuList, type MenuAnchor } from '@/modules/protocols/components/ui';
import { SOP_APPROVAL_STATUS_CONFIG } from '@/modules/sop';
import type { WorkflowCategoryTree } from '@/services/workflow.service';
import type { SOP } from '@/types';
import { sopTitle } from '../format';
import type { TicketSheetSource } from './candidate-tickets-sheet';
import { getProtocolPickerColumns } from './protocol-picker-columns';
import '@/modules/protocols/protocol.css';
import './protocol-picker.css';

const STATUS_OPTIONS = Object.entries(SOP_APPROVAL_STATUS_CONFIG).map(([value, { label }]) => ({ label, value }));
const ACTIVE_OPTIONS = [
	{ label: 'Active', value: 'on' },
	{ label: 'Not active', value: 'off' }
];

/** Each distinct value and how often it occurs, in name order. */
const tally = (values: string[]) => {
	const counts = new Map<string, number>();
	for (const value of values) counts.set(value, (counts.get(value) ?? 0) + 1);
	return [...counts].sort(([a], [b]) => a.localeCompare(b)).map(([name, count]) => ({ name, count }));
};

/** The category tree the Protocols page reads off the server, counted here over the protocols on hand. */
const categoryTreeOf = (sops: SOP[]): WorkflowCategoryTree => ({
	categories: tally(sops.map((sop) => sop.category).filter(Boolean)).map(({ name, count }) => ({
		category: name,
		count,
		subcategories: tally(sops.filter((sop) => sop.category === name).map((sop) => sop.subcategory).filter(Boolean))
	})),
	uncategorized: sops.filter((sop) => !sop.category).length
});

const inCategory = (sop: SOP, { category, subcategory, uncategorized }: CategoryFilter): boolean =>
	uncategorized
		? !sop.category
		: (!category || sop.category === category) && (!subcategory || sop.subcategory === subcategory);

/** The searchable text of a SOP: what the row shows, plus the raw intent slug. */
const searchHaystack = (sop: SOP): string =>
	[sopTitle(sop.intent), sop.intent, sop.category, sop.subcategory].filter(Boolean).join(' ').toLowerCase();

interface ProtocolPickerProps {
	/** The SOPs that hold a protocol's text. */
	sops: SOP[];
	loading: boolean;
	failed: boolean;
	onSelect: (sop: SOP) => void;
	onViewTickets: (sop: SOP, source: TicketSheetSource) => void;
}

export const ProtocolPicker = ({ sops, loading, failed, onSelect, onViewTickets }: ProtocolPickerProps) => {
	const [search, setSearch] = useState('');
	const [category, setCategory] = useState<CategoryFilter>({});
	const [status, setStatus] = useState<string>();
	const [active, setActive] = useState<string>();
	const [menu, setMenu] = useState<{ sop: SOP; anchor: MenuAnchor } | null>(null);

	const terms = useMemo(() => searchTerms(search), [search]);
	const tree = useMemo(() => categoryTreeOf(sops), [sops]);
	const rows = useMemo(
		() =>
			sops.filter(
				(sop) =>
					inCategory(sop, category) &&
					(!status || sop.approvalStatus === status) &&
					(!active || sop.enabled === (active === 'on')) &&
					(!terms.length || matchesAllTerms(searchHaystack(sop), terms))
			),
		[sops, category, status, active, terms]
	);
	const columns = useMemo(
		() =>
			getProtocolPickerColumns({
				terms,
				onOpenMenu: (sop, event) => {
					const rect = event.currentTarget.getBoundingClientRect();
					setMenu((open) =>
						open?.sop.id === sop.id ? null : { sop, anchor: { x: rect.left, y: rect.bottom, right: rect.right, align: 'end' } }
					);
				}
			}),
		[terms]
	);

	const viewTickets = (sop: SOP, source: TicketSheetSource) => {
		setMenu(null);
		onViewTickets(sop, source);
	};

	return (
		<div className='protocol-ui flex h-full flex-col bg-(--surface-white)'>
			<div className='protocol-topbar flex h-15 w-full shrink-0 items-center justify-between gap-5 px-4'>
				<div className='flex min-w-0 items-center gap-5'>
					<h1 className='shrink-0 text-base font-semibold text-(--text-headings)'>Back testing</h1>
					<p className='truncate text-[13px] leading-4.5 text-(--text-secondary)'>
						Every scenario type a protocol has to handle, and the past tickets behind each.
					</p>
				</div>
				<SearchInput
					value={search}
					onChange={(event) => setSearch(event.target.value)}
					placeholder='Search protocols'
					className='h-8 w-70 rounded-full border-(--border-default) pl-9 text-[13px] placeholder:text-[13px] focus-visible:border-(--text-action) focus-visible:ring-0'
				/>
			</div>

			<div className='flex min-h-px flex-1 flex-col overflow-y-auto px-4'>
				<div className='flex flex-wrap items-start gap-2 pb-2'>
					<ProtocolCategoryTabs tree={tree} active={category} onSelect={setCategory} />
					<FilterMenu label='Status' top={0} options={STATUS_OPTIONS} value={status} onChange={setStatus} />
					<FilterMenu label='Active' top={0} options={ACTIVE_OPTIONS} value={active} onChange={setActive} />
				</div>

				{loading ? (
					<div className='flex flex-col gap-2 pt-3'>
						{Array.from({ length: 6 }, (_, index) => (
							<Skeleton key={index} className='h-12 w-full' />
						))}
					</div>
				) : failed ? (
					<p className='pt-6 text-[13px] leading-4.5 text-(--text-secondary)'>Could not load protocols. Please try again.</p>
				) : rows.length === 0 ? (
					<p className='pt-6 text-[13px] leading-4.5 text-(--text-secondary)'>
						{sops.length ? 'No protocols match the search and filters.' : 'No protocols to back test yet.'}
					</p>
				) : (
					<DataTable
						columns={columns}
						data={rows}
						className='bt-protocol-table pb-4'
						enableSorting={false}
						enablePagination={false}
						onRowClick={onSelect}
					/>
				)}
			</div>

			{menu ? (
				<AnchoredPopover anchor={menu.anchor} onClose={() => setMenu(null)}>
					<MenuList>
						<MenuGroup>
							<MenuItem label='View mapped tickets' icon={<Ticket size={16} />} onSelect={() => viewTickets(menu.sop, 'mapped')} />
							{menu.sop.subcategory ? (
								<MenuItem
									label='View subcategory tickets'
									icon={<Tickets size={16} />}
									onSelect={() => viewTickets(menu.sop, 'subcategory')}
								/>
							) : null}
						</MenuGroup>
					</MenuList>
				</AnchoredPopover>
			) : null}
		</div>
	);
};
